import { $createParagraphNode, $getSelection, $isRangeSelection } from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { PhotoIcon, ChevronUpDownIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import TextParagraphIcon from '../icons/TextParagraphIcon';
import TypeH1Icon from '../icons/TypeH1Icon';
import TypeH2Icon from '../icons/TypeH2Icon';
import TypeH3Icon from '../icons/TypeH3Icon';
import ListUnorderedIcon from '../icons/ListUnorderedIcon';
import ListOrderedIcon from '../icons/ListOrderedIcon';
import TextQuoteIcon from '../icons/TextQuoteIcon';
import HorizontalRuleIcon from '../icons/HorizontalRuleIcon';
import { INSERT_COLLAPSIBLE_COMMAND } from './CollapsiblePlugin';
import { INSERT_HTML_COMMAND } from './HtmlPlugin';

export function convertBlock(editor, createNode) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      $setBlocksType(selection, createNode);
    }
  });
}

// Menu order. Text options convert the current block; the rest insert.
export const blockGroups = [
  { key: 'text', heading: 'Text' },
  { key: 'element', heading: 'Element' },
  { key: 'media', heading: 'Media' },
];

export const blockOptions = [
  {
    key: 'paragraph',
    label: 'Normal text',
    keywords: ['paragraph', 'text', 'normal', 'plain'],
    icon: TextParagraphIcon,
    group: 'text',
    onSelect: editor => convertBlock(editor, () => $createParagraphNode()),
  },
  {
    key: 'h1',
    label: 'Heading 1',
    keywords: ['heading', 'h1', 'title', 'large'],
    icon: TypeH1Icon,
    group: 'text',
    onSelect: editor => convertBlock(editor, () => $createHeadingNode('h1')),
  },
  {
    key: 'h2',
    label: 'Heading 2',
    keywords: ['heading', 'h2', 'subtitle', 'medium'],
    icon: TypeH2Icon,
    group: 'text',
    onSelect: editor => convertBlock(editor, () => $createHeadingNode('h2')),
  },
  {
    key: 'h3',
    label: 'Heading 3',
    keywords: ['heading', 'h3', 'small'],
    icon: TypeH3Icon,
    group: 'text',
    onSelect: editor => convertBlock(editor, () => $createHeadingNode('h3')),
  },
  {
    key: 'bullet',
    label: 'Bullet list',
    keywords: ['bullet', 'list', 'ul', 'unordered'],
    icon: ListUnorderedIcon,
    group: 'text',
    onSelect: editor => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
  },
  {
    key: 'numbered',
    label: 'Numbered list',
    keywords: ['numbered', 'list', 'ol', 'ordered'],
    icon: ListOrderedIcon,
    group: 'text',
    onSelect: editor => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
  },
  {
    key: 'quote',
    label: 'Quote',
    keywords: ['quote', 'blockquote', 'citation'],
    icon: TextQuoteIcon,
    group: 'text',
    onSelect: editor => convertBlock(editor, () => $createQuoteNode()),
  },
  {
    key: 'divider',
    label: 'Horizontal rule',
    keywords: ['divider', 'hr', 'line', 'separator', 'horizontal', 'rule'],
    icon: HorizontalRuleIcon,
    group: 'element',
    onSelect: editor => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
  },
  {
    key: 'accordion',
    label: 'Accordion',
    keywords: ['accordion', 'collapsible', 'toggle', 'expand', 'section'],
    icon: ChevronUpDownIcon,
    group: 'element',
    onSelect: editor => editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, {}),
  },
  {
    key: 'html',
    label: 'HTML',
    keywords: ['html', 'code', 'embed', 'custom'],
    icon: CodeBracketIcon,
    group: 'element',
    onSelect: editor => editor.dispatchCommand(INSERT_HTML_COMMAND, undefined),
  },
  {
    key: 'image',
    label: 'Image',
    keywords: ['image', 'picture', 'photo', 'media'],
    icon: PhotoIcon,
    group: 'media',
    onSelect: () => {
      const input = document.getElementById('lexical-image-input');
      if (input) input.click();
    },
  },
];

export function filterBlockOptions(query) {
  if (!query) return blockOptions;
  const q = query.toLowerCase();
  return blockOptions.filter(o =>
    [...o.label.toLowerCase().split(/\s+/), ...o.keywords].some(word => word.startsWith(q)),
  );
}
